import { asRecord } from '@/components/agent-chat/as-record.util';
import {
  countOf,
  textOf,
  type RunProgress,
  type RunState,
} from '@/components/agent-chat/run-progress/run-progress.util';
import {
  isEnrichmentMessage,
  isSmartColumnLeadUpdateMessage,
  isSmartColumnMessage,
  useEnrichmentWebSocket,
  useSmartColumnWebSocket,
} from '@/hooks/use-unified-websocket.hook';
import { parseServerDate } from '@/lib/parse-server-date';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  addOperation,
  selectOperationById,
  setStatus,
  updateProgress,
  type OperationType,
} from '@/store/features/active-operations.feature';
import { useGetEnrichmentSessionStatusQuery } from '@/store/features/enrichment.feature';
import { useGetSmartColumnTaskQuery } from '@/store/features/smart-columns.feature';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useRef, useState } from 'react';

/** One reading of a run: from its result, its status poll or a socket frame. */
interface Reading {
  state: RunState;
  processed: number;
  failed: number;
  total: number;
  startedAt?: number | null;
  completedAt?: number | null;
  emails?: number;
  phones?: number;
}

const TERMINAL: ReadonlySet<RunState> = new Set(['completed', 'failed']);

/** The states that say nothing about progress, kept off the header's
 *  progress indicator. */
const UNREAD: ReadonlySet<RunState> = new Set(['loading', 'unknown']);

/** A run's status poll: every 5s, backing off to a minute while it fails. */
export const POLL_MS = 5000;
const MAX_POLL_MS = 60_000;

export function pollInterval(failures: number): number {
  return Math.min(POLL_MS * 2 ** Math.max(0, failures), MAX_POLL_MS);
}

/** Only a 404 says the run's task is gone; any other error says nothing. */
function isGone(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    (error as { status?: unknown }).status === 404
  );
}

function epoch(value: unknown): number | null {
  return typeof value === 'string' || typeof value === 'number'
    ? (parseServerDate(value)?.getTime() ?? null)
    : null;
}

/**
 * A later reading replaces an earlier one, but an ended run stays ended, and
 * "can't tell" never replaces a run the row has seen going.
 */
function merge(previous: Reading, next: Reading): Reading {
  if (TERMINAL.has(previous.state) && !TERMINAL.has(next.state)) {
    return previous;
  }
  if (next.state === 'unknown' && !UNREAD.has(previous.state)) {
    return previous;
  }
  return {
    ...previous,
    ...next,
    startedAt: next.startedAt ?? previous.startedAt,
    completedAt: next.completedAt ?? previous.completedAt,
  };
}

/**
 * What a started run's result says before anything else answers. A result
 * loaded from the chat's history says nothing about now (the history marks
 * every run done), so it waits for the poll. A live result is running unless
 * it says it already ended.
 */
function initialReading(
  result: Record<string, unknown>,
  pollable: boolean,
  endedStatuses: ReadonlySet<string>
): Reading {
  const total = countOf(result.lead_count);
  const status = textOf(result.status) ?? '';
  const fromHistory = result.__fromHistory === true;
  const startedAt = epoch(result.started_at);
  if (fromHistory && pollable) {
    return { state: 'loading', processed: 0, failed: 0, total, startedAt };
  }
  if (fromHistory || endedStatuses.has(status)) {
    const failed = status === 'failed';
    return {
      state: failed ? 'failed' : 'completed',
      processed: failed ? 0 : total,
      failed: 0,
      total,
      startedAt,
    };
  }
  return { state: 'running', processed: 0, failed: 0, total, startedAt };
}

/**
 * Keeps the reading, registers a live run with the header's progress
 * indicator (it outlives the chat, so a reopened chat finds its start), and
 * reports progress and the end to it.
 */
function useRunReading({
  result,
  operationId,
  operationType,
  label,
  pollable,
  endedStatuses,
  sessionId,
}: {
  result: Record<string, unknown>;
  operationId: string | undefined;
  operationType: OperationType;
  label: string;
  pollable: boolean;
  endedStatuses: ReadonlySet<string>;
  sessionId?: string;
}) {
  const dispatch = useAppDispatch();
  const [reading, setReading] = useState<Reading>(() =>
    initialReading(result, pollable, endedStatuses)
  );
  const [mountedAt] = useState(() => Date.now());
  const fromHistory = result.__fromHistory === true;
  const listId = textOf(result.list_id);
  const persistedOp = useAppSelector((state) =>
    operationId ? selectOperationById(state, operationId) : undefined
  );
  const startedLive = reading.state === 'running' && !fromHistory;

  useEffect(() => {
    if (!operationId || !listId || !startedLive || persistedOp) return;
    dispatch(
      addOperation({
        id: operationId,
        type: operationType,
        label,
        status: 'running',
        progress: { total: reading.total, processed: 0, failed: 0 },
        startedAt: new Date().toISOString(),
        leadListId: listId,
        taskIds: [operationId],
        ...(sessionId ? { sessionId } : {}),
      })
    );
    // Registered once per run (the op then exists); progress follows
    // through updateProgress.
  }, [
    dispatch,
    label,
    listId,
    operationId,
    operationType,
    persistedOp,
    reading.total,
    sessionId,
    startedLive,
  ]);

  const update = useCallback(
    (next: Reading) => {
      setReading((previous) => merge(previous, next));
      if (!operationId || fromHistory || UNREAD.has(next.state)) return;
      dispatch(
        updateProgress({
          id: operationId,
          progress: {
            total: next.total,
            processed: next.processed,
            failed: next.failed,
          },
        })
      );
      if (TERMINAL.has(next.state)) {
        dispatch(
          setStatus({
            id: operationId,
            status: next.state === 'failed' ? 'failed' : 'completed',
          })
        );
      }
    },
    [dispatch, fromHistory, operationId]
  );

  const persistedStart = persistedOp?.startedAt
    ? epoch(persistedOp.startedAt)
    : null;
  const progress: RunProgress = {
    state: reading.state,
    done: reading.processed + reading.failed,
    total: reading.total,
    failed: reading.failed,
    startedAt:
      reading.startedAt ??
      persistedStart ??
      (reading.state === 'running' ? mountedAt : null),
    completedAt: reading.completedAt ?? null,
    ...(reading.emails !== undefined ? { emails: reading.emails } : {}),
    ...(reading.phones !== undefined ? { phones: reading.phones } : {}),
  };
  return { progress, reading, update };
}

/** A status poll's pace, backing off while it fails, and whether its task
 *  is gone (then it stops). */
function usePollBackoff() {
  const [failures, setFailures] = useState(0);
  const [gone, setGone] = useState(false);
  return {
    interval: pollInterval(failures),
    gone,
    setFailures,
    setGone,
  };
}

/**
 * What a failed status poll means. A 404 says the task is gone: a run
 * reloaded from history then reads as the history left it, done, and the
 * poll stops. Any other error (a 503, a network blip, an expired session)
 * says nothing: a reloaded run not yet read shows as neither going nor
 * ended, and the poll keeps asking, slower while it fails. Each failed poll
 * is a new error object; a poll that answers clears it.
 */
function usePollErrors({
  error,
  backoff: { setFailures, setGone },
  state,
  update,
  leadCount,
}: {
  error: unknown;
  backoff: ReturnType<typeof usePollBackoff>;
  state: RunState;
  update: (next: Reading) => void;
  leadCount: number;
}) {
  const unread = UNREAD.has(state);
  const handled = useRef<unknown>(undefined);
  useEffect(() => {
    if (!error) {
      handled.current = undefined;
      setFailures(0);
      return;
    }
    if (handled.current === error) return;
    handled.current = error;
    if (isGone(error)) {
      setGone(true);
      if (unread) {
        update({
          state: 'completed',
          processed: leadCount,
          failed: 0,
          total: leadCount,
        });
      }
      return;
    }
    setFailures((count) => count + 1);
    update({ state: 'unknown', processed: 0, failed: 0, total: leadCount });
  }, [error, leadCount, setFailures, setGone, unread, update]);
}

const SKILL_ENDED: ReadonlySet<string> = new Set(['completed', 'failed']);

/** A skill or AI column run (`smart_column_started`): its task's poll and frames. */
export function useSkillRunProgress(
  result: Record<string, unknown>,
  label: string
): RunProgress {
  const taskId = textOf(result.task_id);
  const { progress, reading, update } = useRunReading({
    result,
    operationId: taskId,
    operationType: 'smart-columns',
    label,
    pollable: Boolean(taskId),
    endedStatuses: SKILL_ENDED,
  });
  const ended = TERMINAL.has(reading.state);
  const leadCount = countOf(result.lead_count);

  const backoff = usePollBackoff();
  const { data, error } = useGetSmartColumnTaskQuery(
    taskId && !ended && !backoff.gone ? taskId : skipToken,
    { pollingInterval: backoff.interval }
  );
  usePollErrors({ error, backoff, state: reading.state, update, leadCount });

  useEffect(() => {
    if (!data) return;
    const state: RunState =
      data.status === 'completed'
        ? 'completed'
        : data.status === 'failed'
          ? 'failed'
          : 'running';
    update({
      state,
      processed: data.processed_leads ?? 0,
      failed: data.failed_leads ?? 0,
      total: data.total_leads || leadCount,
      startedAt: epoch(data.started_at),
      completedAt: TERMINAL.has(state) ? epoch(data.completed_at) : null,
    });
  }, [data, leadCount, update]);

  const onMessage = useCallback(
    (message: unknown) => {
      if (!taskId) return;
      if (isSmartColumnMessage(message) && message.task_id === taskId) {
        const state: RunState =
          message.status === 'completed'
            ? 'completed'
            : message.status === 'failed'
              ? 'failed'
              : 'running';
        update({
          state,
          processed: message.progress.processed ?? 0,
          failed: message.progress.failed ?? 0,
          total: message.progress.total ?? leadCount,
          completedAt: TERMINAL.has(state) ? Date.now() : null,
        });
      } else if (
        isSmartColumnLeadUpdateMessage(message) &&
        message.task_id === taskId
      ) {
        update({
          state: 'running',
          processed: message.progress.processed,
          failed: message.progress.failed,
          total: message.progress.total,
        });
      }
    },
    [leadCount, taskId, update]
  );
  useSmartColumnWebSocket(onMessage);

  return progress;
}

const ENRICHMENT_ENDED: ReadonlySet<string> = new Set([
  'completed',
  'failed',
  'session_completed',
]);

/**
 * An enrichment run (`enrichment_started`): its session's poll and frames.
 * The import-and-enrich envelope names the session by workflow or session id
 * and may carry no task id, so either keys it.
 */
export function useEnrichmentRunProgress(
  result: Record<string, unknown>,
  label: string
): RunProgress {
  const pollId = textOf(result.workflow_id) ?? textOf(result.session_id);
  const runId = textOf(result.task_id) ?? pollId;
  const { progress, reading, update } = useRunReading({
    result,
    operationId: runId,
    operationType: 'enrichment',
    label,
    pollable: Boolean(pollId),
    endedStatuses: ENRICHMENT_ENDED,
    // The id is the session, so the list's reaper may keep the indicator
    // honest after the chat unmounts.
    sessionId: runId,
  });
  const ended = TERMINAL.has(reading.state);
  const leadCount = countOf(result.lead_count);

  const backoff = usePollBackoff();
  const { data, error } = useGetEnrichmentSessionStatusQuery(
    pollId && !ended && !backoff.gone ? pollId : skipToken,
    { pollingInterval: backoff.interval }
  );
  usePollErrors({ error, backoff, state: reading.state, update, leadCount });

  useEffect(() => {
    if (!data) return;
    const state: RunState =
      data.status === 'completed' || data.status === 'completed_with_errors'
        ? 'completed'
        : data.status === 'failed'
          ? 'failed'
          : 'running';
    const total = data.total_prospects || leadCount;
    const failed = data.failed_prospects ?? 0;
    update({
      state,
      processed: Math.min(data.completed_prospects ?? 0, total - failed),
      failed,
      total,
      startedAt: epoch(data.created_at),
      completedAt: TERMINAL.has(state) ? epoch(data.updated_at) : null,
      ...(typeof data.emails_found === 'number'
        ? { emails: data.emails_found }
        : {}),
      ...(typeof data.phones_found === 'number'
        ? { phones: data.phones_found }
        : {}),
    });
  }, [data, leadCount, update]);

  const onMessage = useCallback(
    (message: unknown) => {
      if (!runId || !isEnrichmentMessage(message)) return;
      if (message.task_id !== runId) return;
      const raw = asRecord(message.progress) ?? {};
      const total = countOf(raw.total) || leadCount;
      const failed = countOf(raw.failed);
      const done = countOf(raw.completed) || countOf(raw.processed);
      const state: RunState =
        message.status === 'completed' || message.status === 'session_completed'
          ? 'completed'
          : message.status === 'failed'
            ? 'failed'
            : 'running';
      update({
        state,
        processed: Math.min(done, Math.max(0, total - failed)),
        failed,
        total,
        completedAt: TERMINAL.has(state) ? Date.now() : null,
      });
    },
    [leadCount, runId, update]
  );
  useEnrichmentWebSocket(onMessage);

  return progress;
}
